import React, { useEffect, useMemo, useState } from 'react';
import L from 'leaflet';
import { MapContainer, Marker, Polyline, useMap } from 'react-leaflet';
import { TraffixTileLayer } from '../../../common/map';
import { maneuverRouteLabel } from '../model/catalog';

function FitIntersection({ center, maneuvers }) {
  const map = useMap();
  useEffect(() => {
    const points = maneuvers.flatMap((item) => item.latlngs || []);
    const bounds = L.latLngBounds(points.map((point) => [
      Number(point.lat ?? point[0]),
      Number(point.lng ?? point[1]),
    ]));
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [38, 38], maxZoom: 20 });
    else map.setView(center, 19);
  }, [center, maneuvers, map]);
  return null;
}

function arrowIcon(points, color) {
  if (!points || points.length < 2) return null;
  const from = points[points.length - 2];
  const to = points[points.length - 1];
  const fromLat = Number(from.lat ?? from[0]);
  const fromLng = Number(from.lng ?? from[1]);
  const toLat = Number(to.lat ?? to[0]);
  const toLng = Number(to.lng ?? to[1]);
  const angle = Math.atan2(toLat - fromLat, toLng - fromLng) * (180 / Math.PI);
  return L.divIcon({
    className: 'semafix-editor-arrow-host',
    html: `<span style="color:${color};transform:rotate(${-angle}deg)">▶</span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

function crossingMarker(crossing) {
  return L.divIcon({
    className: 'semafix-crossing-host',
    html: `<span class="semafix-satellite-crossing" aria-hidden="true">
      ${Array.from({ length: 7 }, () => '<i></i>').join('')}
    </span>`,
    iconSize: [48, 20],
    iconAnchor: [24, 10],
  });
}

/**
 * Satellite intersection canvas requested by SEMAFIX v3. The geographic
 * maneuver DTO is used directly, so the preview and editor show the same data.
 */
export default function IntersectionDiagram({
  intersection,
  maneuvers = [],
  crossings = [],
  onOpenEditor,
}) {
  const [hoveredId, setHoveredId] = useState(null);
  const [hoveredCrossingId, setHoveredCrossingId] = useState(null);
  const hovered = maneuvers.find((item) => String(item.id) === String(hoveredId));
  const hoveredCrossing = crossings.find((item) => String(item.id) === String(hoveredCrossingId));
  const center = useMemo(
    () => [Number(intersection?.lat || 44.1024), Number(intersection?.lng || 9.824)],
    [intersection],
  );

  return (
    <div className="semafix-diagram">
      <div className="semafix-diagram-canvas">
        <MapContainer center={center} zoom={19} zoomControl={false} attributionControl={false}>
          <TraffixTileLayer theme="satellite" />
          <FitIntersection center={center} maneuvers={maneuvers} />
          {maneuvers.map((maneuver) => (
            <Polyline
              key={maneuver.id}
              positions={maneuver.latlngs || []}
              pathOptions={{
                color: maneuver.color || '#A1E430',
                weight: hoveredId === maneuver.id ? 9 : 6,
                opacity: hoveredId && hoveredId !== maneuver.id ? 0.25 : 1,
              }}
              eventHandlers={{
                mouseover: () => { setHoveredId(maneuver.id); setHoveredCrossingId(null); },
                mouseout: () => setHoveredId(null),
              }}
            />
          ))}
          {maneuvers.map((maneuver) => {
            const points = maneuver.latlngs || [];
            const icon = arrowIcon(points, maneuver.color || '#A1E430');
            const last = points[points.length - 1];
            return icon && last ? (
              <Marker
                key={`arrow-${maneuver.id}`}
                position={[Number(last.lat ?? last[0]), Number(last.lng ?? last[1])]}
                icon={icon}
                interactive={false}
              />
            ) : null;
          })}
          {crossings.map((crossing) => (
            <Marker
              key={crossing.id}
              position={[crossing.lat, crossing.lng]}
              icon={crossingMarker(crossing)}
              eventHandlers={{
                mouseover: () => { setHoveredCrossingId(crossing.id); setHoveredId(null); },
                mouseout: () => setHoveredCrossingId(null),
              }}
            />
          ))}
        </MapContainer>

        <div className={`semafix-diagram-tooltip${hovered || hoveredCrossing ? ' is-visible' : ''}`}>
          {hovered && (
            <span>{hovered.lanes || 1} corsie · {hovered.laneWidth || 3.25} m</span>
          )}
          {hoveredCrossing && (
            <span>Lunghezza {hoveredCrossing.length} m</span>
          )}
          {!hovered && !hoveredCrossing && (
            <span>Passa su una freccia o su un attraversamento</span>
          )}
        </div>
      </div>
      <aside className="semafix-diagram-legend" aria-label="Legenda manovre">
        <b>Manovre veicolari</b>
        {maneuvers.map((maneuver, index) => (
          <button
            type="button"
            key={maneuver.id}
            aria-label={`Manovra ${maneuver.title}`}
            onMouseEnter={() => { setHoveredId(maneuver.id); setHoveredCrossingId(null); }}
            onMouseLeave={() => setHoveredId(null)}
            onFocus={() => { setHoveredId(maneuver.id); setHoveredCrossingId(null); }}
            onBlur={() => setHoveredId(null)}
          >
            <i style={{ background: maneuver.color || '#A1E430' }}>M{index + 1}</i>
            <span>
              {maneuverRouteLabel(maneuver)}
              <small>{maneuver.lanes || 1} corsie · {maneuver.laneWidth || 3.25} m</small>
            </span>
          </button>
        ))}
        <b>Attraversamenti pedonali</b>
        {crossings.map((crossing, index) => (
          <button
            type="button"
            key={crossing.id}
            aria-label={`Attraversamento ${crossing.name || index + 1}`}
            onMouseEnter={() => { setHoveredCrossingId(crossing.id); setHoveredId(null); }}
            onMouseLeave={() => setHoveredCrossingId(null)}
            onFocus={() => { setHoveredCrossingId(crossing.id); setHoveredId(null); }}
            onBlur={() => setHoveredCrossingId(null)}
          >
            <i className="crossing">P{index + 1}</i>
            <span>{crossing.name || `Attraversamento ${index + 1}`}<small>{crossing.length} m</small></span>
          </button>
        ))}
      </aside>
      <button type="button" className="semafix-diagram-edit" onClick={onOpenEditor}>
        Ottimizza piano semaforico
      </button>
    </div>
  );
}
